import { OpenAPI } from '../../api';

/**
 * Thrown by `downloadCsvExport` with a user-facing message already resolved
 * from the backend's error response (e.g. "too many rows" or "forbidden"),
 * so callers can show it directly in a notification.
 */
export class CsvExportError extends Error {
  readonly status?: number;

  constructor(message: string, status?: number) {
    super(message);
    this.name = 'CsvExportError';
    this.status = status;
  }
}

async function resolveErrorMessage(error: unknown): Promise<CsvExportError> {
  const axiosError = error as {
    response?: { status?: number; data?: unknown };
    message?: string;
  };
  const status = axiosError?.response?.status;

  if (status === 401 || status === 403) {
    return new CsvExportError('No tiene permiso para exportar este listado.', status);
  }

  const data = axiosError?.response?.data;
  if (data instanceof Blob) {
    try {
      const text = await data.text();
      const parsed = JSON.parse(text) as { message?: string | string[] };
      const message = Array.isArray(parsed.message) ? parsed.message.join(' ') : parsed.message;
      if (message) {
        return new CsvExportError(message, status);
      }
    } catch {
      // Response body wasn't JSON — fall through to a generic message below.
    }
  }

  if (status != null) {
    return new CsvExportError(`Error al exportar (código ${status}).`, status);
  }

  return new CsvExportError('Error de red al exportar. Verifique su conexión e intente de nuevo.');
}

/**
 * Downloads a CSV export endpoint as a file in the browser.
 *
 * The generated OpenAPI client doesn't set `responseType: 'blob'` on its
 * requests (see `ProductsService.productsControllerExportToCsv`), so every
 * CSV export in this app bypasses it with a direct axios call instead. This
 * is the one place that lives now, so export features share the same
 * blob-download and error-handling logic instead of re-implementing it.
 */
export async function downloadCsvExport(
  path: string,
  params: Record<string, string | number | undefined>,
  filename: string,
): Promise<void> {
  const axios = (await import('axios')).default;
  const token =
    typeof OpenAPI.TOKEN === 'function' ? await OpenAPI.TOKEN({} as any) : OpenAPI.TOKEN;

  let response;
  try {
    response = await axios.get(`${OpenAPI.BASE}${path}`, {
      responseType: 'blob',
      params,
      headers: {
        Authorization: token ? `Bearer ${token}` : undefined,
      },
    });
  } catch (error) {
    throw await resolveErrorMessage(error);
  }

  const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8' });
  const url = window.URL.createObjectURL(blob);
  try {
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } finally {
    window.URL.revokeObjectURL(url);
  }
}

/** `YYYY-MM-DD` suffix for export filenames, matching the products export convention. */
export function csvExportDateSuffix(): string {
  return new Date().toISOString().split('T')[0];
}
